import { useCallback, useMemo } from 'react';

import { useEstimationQueryActions } from '@/api/hooks/estimationQueryActions';
import { useUserQueryActions } from '@/api/hooks/userQueryActions';
import type { PdfFooter, PdfHeader } from '@/components/organism/DataTable/hooks/exports/model';
import { useAuth } from '@/context/auth';

export const usePdfHeaderFooterDataGetter = (title: string) => {
  const { tenant } = useAuth();
  const { getEstimation } = useEstimationQueryActions();
  const { getUser } = useUserQueryActions();

  const genericPdfHeader = useMemo<PdfHeader>(
    () => ({
      tenantName: tenant?.name ?? '',
      title,
      lightModeLogoUrl: tenant?.logoUrl ?? undefined,
    }),
    [tenant?.name, tenant?.logoUrl, title],
  );

  const getPdfHeaderFooterForEstimation = useCallback(
    async (estimationId: string): Promise<{ header: PdfHeader; footer: PdfFooter }> => {
      const projectWithEstimation = await getEstimation(estimationId);
      const estimationPlanningResponsibleId =
        projectWithEstimation.estimation.planningResponsibleId;
      const user = await getUser(estimationPlanningResponsibleId);
      return {
        header: {
          tenantName: tenant?.name ?? '',
          title,
          lightModeLogoUrl: tenant?.logoUrl ?? undefined,
          projectName: projectWithEstimation.project.name,
          projectDescription: projectWithEstimation.estimation.projectDescription ?? undefined,
        },
        footer: {
          estimationOwnerName: `${user.forename} ${user.surname}`,
          estimationVersionName: projectWithEstimation.estimation.versionName,
          estimationVersionNumber: projectWithEstimation.estimation.versionNumber,
        },
      };
    },
    [getEstimation, getUser, tenant?.name, tenant?.logoUrl, title],
  );

  return {
    genericPdfHeader,
    getPdfHeaderFooterForEstimation,
  };
};
